import { fetchContactImageDefault } from 'features/innovations/business/Startup.api';
import { Media } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC } from 'react';
import { SocialNetwork } from '@actus/types';
import { usePlcApiCall } from '@api/business/api.hook';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { ClippedImage } from '@components/ClippedImage/ClippedImage.component';
import { FacebookLogoIcon } from '@components/icons/social/FacebookLogo.icon';
import { InstagramLogoIcon } from '@components/icons/social/InstagramLogo.icon';
import { LinkedinLogoIcon } from '@components/icons/social/LinkedinLogo.icon';
import { MailLogoIcon } from '@components/icons/social/MailLogo.icon';
import { XLogoIcon } from '@components/icons/social/XLogo.icon';
import { Text } from '@components/Text/Text.component';
import { PlcAnchor } from '@layout/components/PlcAnchor/PlcAnchor.component';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { ClippedImageStyle } from '@utils/images';
import { isExternal } from '@utils/url';
import classNames from 'classnames';
import styles from './InnovationDetailContact.module.scss';

type Props = ComponentPropsWithRef<'main'> & {
  description: string;
  facebook: string;
  image: Media;
  instagram: string;
  linkedin: string;
  mail: string;
  titlebutton: string;
  twitter: string;
  urlbutton: string;
};

export const InnovationDetailContact: FC<Props> = ({
  className,
  description,
  facebook,
  linkedin,
  instagram,
  image,
  mail,
  titlebutton,
  twitter,
  urlbutton,
}) => {
  const contactImageDefault = usePlcApiCall(fetchContactImageDefault, []);
  const rootClass = classNames(className, styles['inno-contact']);
  const isSmartphone = useIsBreakpointDown(Breakpoint.s);

  const socialclass = classNames('horizontal flex align-center plc-my-s');
  const socialmobileclass = classNames(`${styles['margin-top-zero']} horizontal flex align-center plc-my-s'`);
  const instagramURL = instagram && (isExternal(instagram) ? instagram : 'https://'.concat(instagram));
  const linkedinURL = linkedin && (isExternal(linkedin) ? linkedin : 'https://'.concat(linkedin));
  const facebookURL = facebook && (isExternal(facebook) ? facebook : 'https://'.concat(facebook));
  const twitterURL = twitter && (isExternal(twitter) ? twitter : 'https://'.concat(twitter));

  const mailclass = classNames(`${styles['contact-flex']} ${styles['mail']} horizontal align-center plc-my-s'`);
  let imageContact = image;
  if (contactImageDefault.data?.media) {
    imageContact = image ? image : contactImageDefault.data?.media;
  }
  return (
    <div className={rootClass}>
      <div className={styles["grid-contact-parent"]}>
        <div className={styles["grid-contact-header"]}>
          <Text className="h2-up-s plc-py-m" i18nKey="innovations.detail.contact" tag="h2" variant="h3" />
          <Text className="slide-description plc-pb-m pre-line" tag="p" variant="p">
            {description}
          </Text>

          <PlcAnchor linkProps={{ as: urlbutton, href: urlbutton }} target="_blank">
            <PrimaryButton>{titlebutton}</PrimaryButton>
          </PlcAnchor>
          {mail && (
            <div className={mailclass}>
              <MailLogoIcon size={30} />
              <Text className="plc-px-s" tag="p" variant="small">
                <a href={`mailto:${mail}`}>{mail}</a>
              </Text>
            </div>
          )}
          <div className={isSmartphone ? socialmobileclass : socialclass}>
            {instagramURL && (
              <PlcAnchor className="plc-mr-s" linkProps={{ as: instagramURL, href: instagramURL }} target="_blank">
                <InstagramLogoIcon size={40} title="Instagram" />
              </PlcAnchor>
            )}
            {linkedinURL && (
              <PlcAnchor className="plc-mr-s" linkProps={{ as: linkedinURL, href: linkedinURL }} target="_blank">
                <LinkedinLogoIcon size={40} title={SocialNetwork.Linkedin} />
              </PlcAnchor>
            )}
            {facebookURL && (
              <PlcAnchor className="plc-mr-s" linkProps={{ as: facebookURL, href: facebookURL }} target="_blank">
                <FacebookLogoIcon size={40} title={SocialNetwork.Facebook} />
              </PlcAnchor>
            )}
            {twitterURL && (
              <PlcAnchor className="plc-mr-s" linkProps={{ as: twitterURL, href: twitterURL }} target="_blank">
                <XLogoIcon size={40} title={SocialNetwork.X} />
              </PlcAnchor>
            )}
          </div>
        </div>
        <div className={`plc-pt-basis ${styles['grid-contact-img']}`}>
          <ClippedImage
            alt={imageContact?.alt}
            caption={imageContact?.alt}
            clipStyle={ClippedImageStyle.innovationContact}
            plcSrc={{ src: imageContact?.url, type: 'external' }}
          />
        </div>
      </div>
    </div>
  );
};
